import {Box3, Raycaster, Vector3} from "three";
import * as Globals from "../../../globals";
import * as nameConstants from "../../../constants/nameConstants";
import {RoofOrientation} from "../../../constants/roofConstants";
import {buildingGroup} from "../../../main";
import {getRaehmeByOrientation} from "../../../utils/configuratorUtils";
import {findChildrenByName} from "../../../utils/threeUtils";
import {sortBySubKey, toFixedFloat, toFixedVector} from "../../../utils/utils";
import {t} from "../../../../i18n";

/**
 * Für die Berechnung aller tragenden Positionen/Wände im aktuellen Gebäude für ein Dach in Ausrichtung (roofOrientation).
 * Relevant sind dabei nur Elemente die im 90° Winkel zum Dach verlaufen und das Dach auf voller Länge an ihrer Position abstützen.
 * Dafür werden die Schnittpunkte des Dachs mit den jeweiligen Elementen bestimmt und zu einer Range zusammengefasst.
 * Dies wird für das Dach und die Wände gemacht. Anschließend werden diese noch gefiltert und sortiert.
 * */

export class SpanPositionsCalculator {
    constructor(roof, roofOrientation, allRaehme) {
        this.roof = roof;
        this.roofOrientation = roofOrientation;
        this.roofThickness = new Box3().setFromObject(this.roof).getSize(new Vector3()).y;
        this.supportsOrientation = roofOrientation === RoofOrientation.XAxis ? "z" : "x";
        this.allRaehme = allRaehme;
        this.spanPositions = [];

        this.initRaycasters();
    }

    initRaycasters() {
        this.raehmRaycaster = new Raycaster();
        this.raehmRaycaster.params.Mesh.threshold = 0.05;
        this.raehmRaycaster.layers.enable(Globals.layers.raehm);
        this.raehmRaycaster.layers.enable(Globals.layers.supportBeam);

        this.roofRaycaster = new Raycaster();
        this.roofRaycaster.params.Mesh.threshold = 0.05;
        this.roofRaycaster.layers.enableAll();
    }

    getSpanRelevantPositions() {
        for (let i = 0; i < this.roof.children.length; i++) {
            this.roof.children[i].updateMatrix();
        }

        let buildingBounds = new Box3().setFromObject(buildingGroup).expandByVector(new Vector3(1, 0, 1)); //Expanden um Raycaster außerhalb ansetzen zu können
        let supportOrientedRaehme = getRaehmeByOrientation(this.supportsOrientation, this.allRaehme); //Alle über Kreuz laufenden Wände

        let roofBBoxes = findChildrenByName(this.roof, nameConstants.wallMeshName, true, false);
        for (let i = 0; i < roofBBoxes.length; i++) {
            roofBBoxes[i].updateMatrix();
        }

        for (let i = 0; i < supportOrientedRaehme.length; i++) {
            let raehm = supportOrientedRaehme[i]; //Aktuell gekreuzter Rähm
            raehm.updateMatrix();
            let raehmBounds = new Box3().setFromObject(raehm);
            let raehmMaxY = raehmBounds.max.y;
            let raycasterYPosition = raehmMaxY - (this.roofThickness * 0.5);
            let raycasterValues = this.getRaycasterValues(buildingBounds, this.supportsOrientation, raehmBounds, raycasterYPosition); //Werte um Raycaster in beide Richtungen durch die Wand zu schießen

            this.raehmRaycaster.set(raycasterValues.origin, raycasterValues.direction);
            let intersections1 = this.raehmRaycaster.intersectObjects(this.allRaehme, false); //Schnittpunkte Richtung 1 mit den Wänden
            this.raehmRaycaster.set(raycasterValues.invertedOrigin, raycasterValues.invertedDirection);
            let intersections2 = this.raehmRaycaster.intersectObjects(this.allRaehme, false); //Schnittpunkte Richtung 2 mit den Wänden

            this.roofRaycaster.set(raycasterValues.origin, raycasterValues.direction);
            let roofIntersections1 = this.roofRaycaster.intersectObjects(roofBBoxes, false); //Schnittpunkte Richtung 1 mit den Wänden
            this.roofRaycaster.set(raycasterValues.invertedOrigin, raycasterValues.invertedDirection);
            let roofIntersections2 = this.roofRaycaster.intersectObjects(roofBBoxes, false); //Schnittpunkte Richtung 2 mit den Wänden

            if (roofIntersections1.length !== roofIntersections2.length) {
                throw new Error(t("roof_span_mismatch"));
            }

            let wallRanges = this.getRangesFromIntersection(intersections1, intersections2, this.supportsOrientation);
            let roofRanges = this.getRangesFromIntersection(roofIntersections1, roofIntersections2, this.supportsOrientation);

            if (wallRanges.length === 0 || roofRanges.length === 0) {
                throw new Error(t('roof_range_unknown'));
            }

            for (let j = 0; j < wallRanges.length; j++) {
                wallRanges[j].start = toFixedVector(wallRanges[j].start);
                wallRanges[j].end = toFixedVector(wallRanges[j].end);
            }

            for (let j = 0; j < roofRanges.length; j++) {
                roofRanges[j].start = toFixedVector(roofRanges[j].start);
                roofRanges[j].end = toFixedVector(roofRanges[j].end);
                let raehmSize = raehmBounds.getSize(new Vector3());
                roofRanges[j].thickness = this.supportsOrientation === "x" ? raehmSize.z : raehmSize.x;
            }

            if (this.supportsOrientation === "x") {
                if (wallRanges[0].start.x === roofRanges[0].start.x && wallRanges[0].end.x === roofRanges[0].end.x) {
                    this.spanPositions.push(roofRanges[0]);
                }
            } else {
                if (wallRanges[0].start.z === roofRanges[0].start.z && wallRanges[0].end.z === roofRanges[0].end.z) {
                    this.spanPositions.push(roofRanges[0]);
                }
            }
        }

        this.filterDuplicateSpanPositions();
        this.sortIntersectsByPosition();

        //Checken, ob das ganze Dach gestützt ist. Sonst keine spanPositions zurückgeben
        if (this.spanPositions.length > 0) {
            let size = new Box3().setFromObject(this.roof).getSize(new Vector3());

            let firstPosition = this.spanPositions[0];
            let lastPosition = this.spanPositions[this.spanPositions.length - 1];

            let totalSpan;
            if (this.roofOrientation === RoofOrientation.XAxis) {
                size = size.x;
                totalSpan = lastPosition.start.x - firstPosition.start.x;
            } else {
                size = size.z;
                totalSpan = lastPosition.start.z - firstPosition.start.z;
            }

            totalSpan = Math.abs(totalSpan) + 0.1; //Eine Wandstärke aufrechnen. Positionen liegen immer auf einer Seite der Wand, daher entsteht eine Größe

            //Kein gültiges Dach. Wird nicht komplett gestützt
            //TODO: nur Temp Fix. Sollte richtig gelöst werden
            if (toFixedFloat(Math.abs(size - totalSpan), 3) > 0.03) {
                // if (toFixedFloat(totalSpan) !== toFixedFloat(size)) {
                this.spanPositions = [];
            }
        }

        return this.spanPositions;
    }

    filterDuplicateSpanPositions() {
        for (let i = this.spanPositions.length - 1; i >= 0; i--) {
            let spanPosition1 = this.spanPositions[i];
            for (let j = this.spanPositions.length - 1; j >= 0; j--) {
                let spanPosition2 = this.spanPositions[j];
                if (i === j) {
                    continue;
                }

                if (spanPosition1.start.equals(spanPosition2.start) && spanPosition1.end.equals(spanPosition2.end)) {
                    this.spanPositions[i].thickness = spanPosition1.thickness < spanPosition2.thickness ? spanPosition1.thickness : spanPosition2.thickness;
                    this.spanPositions[j].thickness = spanPosition1.thickness < spanPosition2.thickness ? spanPosition1.thickness : spanPosition2.thickness;
                    this.spanPositions.splice(i, 1);
                    break;
                }
            }
        }
    }

    getRangesFromIntersection(intersections1, intersections2, axis) {
        let occupiedRanges = [];
        intersections2 = intersections2.reverse();

        //Ranges auslesen
        for (let i = 0; i < intersections1.length; i++) {
            let startPoint = intersections1[i].point;
            let object1 = intersections1[i].object;
            let secondIndex = i;
            if (intersections2[i] !== undefined && object1 !== intersections2[i].object) {
                for (let j = 0; j < intersections2.length; j++) {
                    if (object1 === intersections2[j].object) {
                        secondIndex = j;
                        break;
                    }
                }
            }

            let endPoint = intersections2[secondIndex].point;

            occupiedRanges.push({start: toFixedVector(startPoint), end: toFixedVector(endPoint)});
        }

        //Zusammenhängende Ranges zusammenfassen
        if (occupiedRanges.length <= 1) {
            return occupiedRanges;
        }

        occupiedRanges = this.combineOverlappingRanges(occupiedRanges, axis);

        return occupiedRanges;
    }

    combineOverlappingRanges(data, axis) {
        if (axis !== 'x' && axis !== 'z') {
            throw new Error("Invalid axis. Allowed values are 'x' or 'z'.");
        }

        // Sort the data based on the specified axis
        const sortedData = data.slice().sort((a, b) => a.start[axis] - b.start[axis]);

        const result = [];
        let currentRange = null;

        for (const item of sortedData) {
            if (!currentRange) {
                currentRange = {...item};
            } else {
                const currentEnd = currentRange.end[axis];
                const itemStart = item.start[axis];
                const itemEnd = item.end[axis];

                // Check for overlap
                if (currentEnd >= itemStart) {
                    // Combine the ranges
                    currentRange.end[axis] = Math.max(currentEnd, itemEnd);
                } else {
                    // No overlap, add the current range to the result and update currentRange
                    result.push({...currentRange});
                    currentRange = {...item};
                }
            }
        }

        // Append the last range
        if (currentRange) {
            result.push(currentRange);
        }

        return result;
    }


    sortIntersectsByPosition() {
        /*Sortierung der Intersections in aufsteigender Reihenfolge ihrer Position in Dachausrichtung*/
        //TODO: Sortierung muss für intersects und walls sein
        if (this.roofOrientation === RoofOrientation.XAxis) {
            this.spanPositions = sortBySubKey(this.spanPositions, "start", "x");
        } else {
            this.spanPositions = sortBySubKey(this.spanPositions, "start", "z");
        }

        return this.spanPositions;
    }

    //Hilfsfunktionen
    getRaycasterValues(buildingBounds, orientation, raehmBounds, yPosition) {
        let origin, direction, invertedOrigin, invertedDirection;
        let raehmCenter = raehmBounds.getCenter(new Vector3());
        if (orientation === "x") {
            origin = new Vector3(buildingBounds.min.x, yPosition, raehmCenter.z);
            invertedOrigin = new Vector3(buildingBounds.max.x, yPosition, raehmCenter.z);
            direction = new Vector3(1, 0, 0);
            invertedDirection = new Vector3(-1, 0, 0);
        } else {
            origin = new Vector3(raehmCenter.x, yPosition, buildingBounds.min.z);
            invertedOrigin = new Vector3(raehmCenter.x, yPosition, buildingBounds.max.z);
            direction = new Vector3(0, 0, 1);
            invertedDirection = new Vector3(0, 0, -1);
        }

        return {
            origin: origin,
            direction: direction,
            invertedOrigin: invertedOrigin,
            invertedDirection: invertedDirection
        };
    }
}
